import { GigType } from "model/Gig";

import type {
  FormData,
  RecruitFormData,
  SelectedWorker,
} from "../modal/gigFormModal.types";

export const GIG_DESCRIPTION_MAX_LENGTH = 2056;

type ValidateGigFormInput = {
  formData: FormData;
  recruitFormData: RecruitFormData;
  selectedWorkers: SelectedWorker[];
  showOverlappingError: boolean;
};

type ValidationFactsInput = Omit<ValidateGigFormInput, "showOverlappingError">;

type DisabledReasonKey =
  | "CreateDisabledWorkersMissing"
  | "CreateDisabledWorkersMissingTeam"
  | "CreateDisabledShiftsMissing"
  | "CreateDisabledOverlappingShifts"
  | "CreateDisabledRoleMissing"
  | "CreateDisabledWorkGroupMissing"
  | "CreateDisabledRatesInvalid"
  | "CreateDisabledCompleteRequiredFields";

type ValidationFacts = {
  shiftsOk: boolean;
  descOk: boolean;
  hasWorkers: boolean;
  hasWorkersWithTeams: boolean;
  roleOk: boolean;
  workGroupOk: boolean;
  ratesOk: boolean;
};

const assertUnreachableGigType = (gigType: never): never => {
  throw new Error(`Unhandled create gig type: ${String(gigType)}`);
};

const computeValidationFacts = (
  input: ValidationFactsInput,
): ValidationFacts => {
  const shiftsOk = (input.formData.shifts?.length ?? 0) > 0;
  const descOk =
    (input.formData.description?.length ?? 0) <= GIG_DESCRIPTION_MAX_LENGTH;
  const hasWorkers = input.selectedWorkers.length > 0;
  const hasWorkersWithTeams =
    hasWorkers &&
    input.selectedWorkers.every(
      (worker) =>
        worker.selectedTeamId !== null && worker.selectedTeamId !== undefined,
    );
  const roleOk =
    typeof input.recruitFormData.role === "string" &&
    input.recruitFormData.role.trim().length > 0 &&
    input.recruitFormData.role.length <= 24;
  const workGroupOk =
    input.recruitFormData.workGroupId !== null &&
    input.recruitFormData.workGroupId !== undefined;
  const min = Number(input.recruitFormData.minRate);
  const max = Number(input.recruitFormData.maxRate);
  const ratesOk = Number.isFinite(min) && Number.isFinite(max) && max >= min;

  return {
    shiftsOk,
    descOk,
    hasWorkers,
    hasWorkersWithTeams,
    roleOk,
    workGroupOk,
    ratesOk,
  };
};

/**
 * Validates gig form state based on gig type specific business rules.
 */
export const validateGigForm = ({
  formData,
  recruitFormData,
  selectedWorkers,
  showOverlappingError,
}: ValidateGigFormInput): boolean => {
  const {
    shiftsOk,
    descOk,
    hasWorkersWithTeams,
    roleOk,
    workGroupOk,
    ratesOk,
  } = computeValidationFacts({
    formData,
    recruitFormData,
    selectedWorkers,
  });
  if (!shiftsOk || !descOk) return false;

  switch (formData.type) {
    case GigType.SOLO:
      return hasWorkersWithTeams && !showOverlappingError;
    case GigType.WORKPOOL:
      return roleOk && workGroupOk;
    case GigType.PUBLIC:
      return roleOk && workGroupOk && ratesOk;
  }

  return assertUnreachableGigType(formData.type);
};

/**
 * Returns translation keys for why create action is disabled.
 * Mirrors validateGigForm rules per gig type.
 */
export const getCreateDisabledReasonKeys = ({
  formData,
  recruitFormData,
  selectedWorkers,
  showOverlappingError,
}: ValidateGigFormInput): DisabledReasonKey[] => {
  const reasons: DisabledReasonKey[] = [];
  const {
    shiftsOk,
    descOk,
    hasWorkers,
    hasWorkersWithTeams,
    roleOk,
    workGroupOk,
    ratesOk,
  } = computeValidationFacts({
    formData,
    recruitFormData,
    selectedWorkers,
  });

  if (!shiftsOk) {
    reasons.push("CreateDisabledShiftsMissing");
  }
  if (!descOk) {
    reasons.push("CreateDisabledCompleteRequiredFields");
  }

  switch (formData.type) {
    case GigType.SOLO:
      if (!hasWorkers) {
        reasons.push("CreateDisabledWorkersMissing");
      } else if (!hasWorkersWithTeams) {
        reasons.push("CreateDisabledWorkersMissingTeam");
      }
      if (showOverlappingError) {
        reasons.push("CreateDisabledOverlappingShifts");
      }
      break;
    case GigType.WORKPOOL:
      if (!roleOk) {
        reasons.push("CreateDisabledRoleMissing");
      }
      if (!workGroupOk) {
        reasons.push("CreateDisabledWorkGroupMissing");
      }
      break;
    case GigType.PUBLIC:
      if (!roleOk) {
        reasons.push("CreateDisabledRoleMissing");
      }
      if (!workGroupOk) {
        reasons.push("CreateDisabledWorkGroupMissing");
      }
      if (!ratesOk) {
        reasons.push("CreateDisabledRatesInvalid");
      }
      break;
  }

  if (reasons.length === 0) {
    reasons.push("CreateDisabledCompleteRequiredFields");
  }

  return [...new Set(reasons)];
};
